<template>
    <div v-show="isVisible" class="qa-banner-add-annotation__new-annotation-dot" :style="style">
        <Poptip v-model="isVisible" placement="bottom-start" width="440" transfer>
            <template #content>
                <QaAnnotationInputForm
                    v-if="isVisible"
                    ref="inputForm"
                    :is-saving="isAddingAnnotation"
                    margin-bottom="none"
                    poptip-width="wide"
                    @cancel="close"
                    @save="saveAnnotation"
                />
            </template>
        </Poptip>
    </div>
</template>

<script lang="ts">
import { type PropType } from "vue";

import QaAnnotationInputForm from "@/components/Qa/QaAnnotationInputForm.vue";
import { addAnnotationToDeliverable } from "@/services/Qa";
import type { ConstructedDeliverable } from "@/types/types";

export default {
    components: {
        QaAnnotationInputForm
    },

    props: {
        deliverable: {
            type: Object as PropType<ConstructedDeliverable>,
            required: true
        },
        position: {
            type: Object as PropType<{ x: number; y: number }>,
            validator(value: { x: number; y: number }) {
                return value.x !== undefined && value.y !== undefined;
            }
        },
        scalingFactor: {
            type: Number,
            default: 1
        },
        timeline: {
            type: Number
        },
        visible: {
            type: Boolean,
            default: false
        }
    },

    emits: ["annotationAdded", "close"],

    data() {
        return {
            isAddingAnnotation: false,
            isVisible: false
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        style() {
            if (!this.position) {
                return {};
            }

            return {
                left: `${this.position.x * this.scalingFactor}px`,
                top: `${this.position.y * this.scalingFactor}px`
            };
        }
    },

    watch: {
        visible(newVal) {
            this.isVisible = newVal;
        },

        isVisible: {
            handler(newValue) {
                if (newValue) {
                    this.$nextTick(() => {
                        (this.$refs.inputForm as HTMLDivElement).focus();
                    });
                }
            }
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        async saveAnnotation({ content, mentions }: { content: string; mentions: string[] }) {
            this.isAddingAnnotation = true;

            if (!this.position) {
                return;
            }

            try {
                const annotation = await addAnnotationToDeliverable(
                    this.campaignId,
                    this.deliverable,
                    content,
                    this.timeline,
                    this.position.x,
                    this.position.y,
                    mentions
                );

                this.$emit("annotationAdded", annotation);
                this.close();
                this.$snackbar.success("Annotation added.");
            } catch (err) {
                console.error(err);

                this.$snackbar.error(
                    "There was an unexpected error and the annotation was unable to be saved. Please try again in a few moments."
                );
            }

            this.isAddingAnnotation = false;
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

$new-annotation-dot-size: 30px;

.qa-banner-add-annotation__new-annotation-dot {
    align-items: flex-end;
    background-color: rgba(127, 127, 127, 0.75);
    border-radius: 100%;
    border: 1px dashed $white;
    cursor: default;
    display: flex;
    height: $new-annotation-dot-size;
    margin: (0 - calc($new-annotation-dot-size / 2)) 0 0 (0 - calc($new-annotation-dot-size / 2));
    padding: 0 calc($new-annotation-dot-size / 2) 0 0;
    position: relative;
    width: $new-annotation-dot-size;
}
</style>
